import * as React from 'react'
import featureFlagsReducer from './reducers/featureFlags'
import reducerMisc from './reducers/reducerMisc'
import reducersAPI from './reducers/reducersApi'
import {
  APP_NAME,
  INITIAL_LOCAL_STORAGE_APP_STATE,
  LOCAL_STORAGE_APP_CONTEXT_DATA_VAR_NAME,
  LOCAL_STORAGE_COMPANY,
  LOCAL_STORAGE_CURRENT_COMPANY_REFERENCE,
  LOCAL_STORAGE_NOTIFICATIONS,
  LOCAL_STORAGE_SELECTED_COMPANY_VAR_NAME,
  LOCAL_STORAGE_USER,
  LOCAL_STORAGE_WIZARD_DATA,
  LOCAL_STORAGE_WIZARD_SAVED_DATA,
  WIZARD_STAGES_ID,
  WIZARD_STAGES_SEQUENCE
} from 'utils/constants'
import { appLocalStorage } from 'utils/misc'
import { COMPANY_BALANCE_INITIAL_STATE, USER_INITIAL_STATE } from './reducers/constants'
import { getUserFromStateOrLocalStorage } from './reducers/helpers'
import {
  IAppContext,
  IAppContextProvider,
  ICompany,
  ICompanyStages,
  IDashboardMetricsSettingsByCompany,
  IPaymentLinksFiltersByCompany,
  IRegistrationInitialState,
  ISelectedCompany,
  ISidePanelContent,
  IWizardData,
  IWizardSavedData
} from './interface'
import { IFeatureFlagStore } from './reducers/featureFlags/interface'
import { initialStatesAPICombined } from './reducers/reducersAPIAndInitialStates'
import { LOCAL_STORAGE_APP_STATE } from './constants'

const AppContext = React.createContext({} as IAppContextProvider)

const AppContextProvider = ({ children }: IAppContext) => {
  const { useEffect, useReducer, useState } = React

  // API ···············································································································
  const [appStore, dispatch] = useReducer(reducersAPI, initialStatesAPICombined)
  const API = { appStore, dispatch }

  // FEATURE FLAGS ·····································································································
  const [flagsStore, flagsDispatch] = useReducer(featureFlagsReducer, {} as IFeatureFlagStore)
  const FLAGS = { flagsStore, flagsDispatch }

  // DASHBOARD SETTINGS BY COMPANY ·····················································································
  const [dashboardAcceptanceSettingsByCompany, setDashboardAcceptanceSettingsByCompany] =
    useState<IDashboardMetricsSettingsByCompany>({})
  const [dashboardRiskSettingsByCompany, setDashboardRiskSettingsByCompany] =
    useState<IDashboardMetricsSettingsByCompany>({})
  const [dashboardSalesSettingsByCompany, setDashboardSalesSettingsByCompany] =
    useState<IDashboardMetricsSettingsByCompany>({})

  const changeDashboardAcceptanceSettingsByCompany = (value: IDashboardMetricsSettingsByCompany) =>
    setDashboardAcceptanceSettingsByCompany(value)
  const changeDashboardRiskSettingsByCompany = (value: IDashboardMetricsSettingsByCompany) =>
    setDashboardRiskSettingsByCompany(value)
  const changeDashboardSalesSettingsByCompany = (value: IDashboardMetricsSettingsByCompany) =>
    setDashboardSalesSettingsByCompany(value)

  const DASHBOARD_ACCEPTANCE_SETTINGS_BY_COMPANY = {
    dashboardAcceptanceSettingsByCompany,
    changeDashboardAcceptanceSettingsByCompany
  }
  const DASHBOARD_RISK_SETTINGS_BY_COMPANY = {
    dashboardRiskSettingsByCompany,
    changeDashboardRiskSettingsByCompany
  }
  const DASHBOARD_SALES_SETTINGS_BY_COMPANY = {
    dashboardSalesSettingsByCompany,
    changeDashboardSalesSettingsByCompany
  }

  // ESIGN ·············································································································
  const [esign, setEsign] = useState(false)
  const toggleEsign = (value: boolean) => setEsign(value)
  const ESIGN = { esign, toggleEsign }

  // FOOTER ············································································································
  const [footerClasses, setFooterClasses] = useState('')
  const [footerContent, setFooterContent] = useState(<></>)
  const [showFooter, setShowFooter] = useState(false)
  const changeFooterClasses = (classes: string) => setFooterClasses(classes)
  const changeFooterContent = (content: any) => setFooterContent(content)
  const changeShowFooter = (value: boolean) => setShowFooter(value)
  const FOOTER = {
    changeFooterClasses,
    changeFooterContent,
    changeShowFooter,
    footerClasses,
    footerContent,
    showFooter
  }

  // APP DATA CONTEXT ··································································································
  const [appData, setAppData] = useReducer(reducerMisc, LOCAL_STORAGE_APP_STATE || INITIAL_LOCAL_STORAGE_APP_STATE)
  const changeOrAddValueToAppData = (value: any) => setAppData(value)
  useEffect(() => {
    appLocalStorage.setValue(LOCAL_STORAGE_APP_CONTEXT_DATA_VAR_NAME, appData)
  }, [appData])
  const APP_DATA_CONTEXT = { appData, APP_NAME, changeOrAddValueToAppData }

  // PLAYGROUND ········································································································
  const [playground, setPlayground] = useState(true)
  const togglePlayground = (value: boolean) => setPlayground(value)
  const PLAYGROUND = { playground, togglePlayground }

  // COMPANY ···········································································································
  // MISC DATA
  const COMPANY_INITIAL_STATE: ICompany = {
    adminRedirectUrl: '',
    balance: COMPANY_BALANCE_INITIAL_STATE,
    entityId: '',
    idReferenceCompany: '',
    idParentCompany: '',
    isActive: false,
    name: '',
    riskValidation: '',
    testMode: false,
    totalBalance: '',
    userActivitiesPermissionsByEntity: [],
    userRoleName: '',
    commercialInfo: {
      accountLevel: ''
    },
    isParentPartner: false
  }
  const [currentCompanyReference, setCurrentCompanyReference] = useState(
    appLocalStorage.getValue(LOCAL_STORAGE_CURRENT_COMPANY_REFERENCE)
  )
  const updateCurrentCompanyReference = (newReferenceId: string) => {
    appLocalStorage.setValue(LOCAL_STORAGE_CURRENT_COMPANY_REFERENCE, newReferenceId)
    setCurrentCompanyReference(newReferenceId)
  }
  const [company, setCompany] = useState(COMPANY_INITIAL_STATE)
  const companyReset = () => {
    setCompany(COMPANY_INITIAL_STATE)
    setAppData({ [LOCAL_STORAGE_COMPANY]: COMPANY_INITIAL_STATE })
  }
  const companyUpdate = (values: any) => {
    const NEW_COMPANY_DATA = { ...company, ...values }
    setCompany(NEW_COMPANY_DATA)
    setAppData({ [LOCAL_STORAGE_COMPANY]: NEW_COMPANY_DATA })
  }

  const isFirstLoadEntity = (entityId: string): boolean => {
    const { firstLoadEntities = {} } = appData as any
    const isFirstLoad = firstLoadEntities[entityId] === undefined
    setAppData({ firstLoadEntities: { ...firstLoadEntities, [entityId]: isFirstLoad } })
    return isFirstLoad
  }
  // NEW
  const REGISTRATION_INITIAL_STATE: IRegistrationInitialState = { active: 0, complete: [] }
  const [registration, setRegistration] = useState(REGISTRATION_INITIAL_STATE)
  const saveRegistration = (active: number, complete: Array<number>) => setRegistration({ active, complete })
  const registerStep = (stepId: number) => {
    const COMPLETE = registration.complete
    COMPLETE.push(stepId)
    const NEXT_STEP = stepId + 1
    setRegistration({ active: NEXT_STEP, complete: COMPLETE })
  }
  const resetRegistration = () => setRegistration(REGISTRATION_INITIAL_STATE)
  // SELECTED
  const COMPANY_EMPTY: ISelectedCompany = {
    id: '',
    isActive: false,
    isTest: false,
    mcc: '',
    name: '',
    riskValidation: '',
    taxpayer: '',
    userActivitiesPermissionsByEntity: [],
    userRoleName: ''
  }
  const getSelectedCompanyFromStateOrLocalStorage = () => {
    const SELECTED_COMPANY_IN_LOCALSTORAGE = LOCAL_STORAGE_APP_STATE[LOCAL_STORAGE_SELECTED_COMPANY_VAR_NAME] || {}

    if (SELECTED_COMPANY_IN_LOCALSTORAGE.id || false) {
      return SELECTED_COMPANY_IN_LOCALSTORAGE
    } else {
      return COMPANY_EMPTY
    }
  }
  const [selectedCompany, setSelectedCompany] = useState(getSelectedCompanyFromStateOrLocalStorage())
  const resetSelectedCompany = () => {
    setSelectedCompany(COMPANY_EMPTY)
    setAppData({ [LOCAL_STORAGE_SELECTED_COMPANY_VAR_NAME]: COMPANY_EMPTY })
  }
  const updateSelectedCompany = (properties: ISelectedCompany) => {
    const COMPANY = {
      ...selectedCompany,
      ...properties
    }
    setSelectedCompany(COMPANY)
    setAppData({ [LOCAL_STORAGE_SELECTED_COMPANY_VAR_NAME]: COMPANY })
  }
  // STAGES
  const [companyStages, setCompanyStages] = useState<Array<ICompanyStages>>([])
  const updateCompanyStages = (stages: Array<ICompanyStages>) => setCompanyStages(stages)

  // WIZARD
  const WIZARD_DATA_INITIAL_STATE: IWizardData = {
    documents: [],
    commercialInfo: { commercialName: '', externalSalesforceId: '', categoryMcc: '', expectedGpv: 0, employeeCount: 0 },
    clabe: '',
    currentStage: 0,
    entityId: '',
    externalAccounts: [{ reference: '' }],
    fiscalInfo: {
      externalNumber: '',
      fiscalType: '',
      municipality: '',
      neighborhood: '',
      rfc: '',
      state: '',
      street: '',
      zip: '',
      businessPhone: ''
    },
    hasData: false,
    idReferenceCompany: '',
    idParentCompany: '',
    legalInfo: { legalRepresentativeName: '', legalRepresentativeEmail: '' },
    mcc: '',
    name: '',
    planSelected: '',
    riskLevel: '',
    signatureType: '',
    stageOnPlate: { id: 0, name: WIZARD_STAGES_ID.evaluation },
    stages: []
  }
  const getWizardDataFromStateOrLocalStorage = () => {
    const WIZARD_DATA_IN_LOCALSTORAGE = LOCAL_STORAGE_APP_STATE[LOCAL_STORAGE_WIZARD_DATA] || {}

    if (WIZARD_DATA_IN_LOCALSTORAGE.hasData) {
      return WIZARD_DATA_IN_LOCALSTORAGE
    } else {
      return WIZARD_DATA_INITIAL_STATE
    }
  }
  const [wizardData, setWizardData] = useState(getWizardDataFromStateOrLocalStorage())
  const updateWizardData = (data: any) => {
    setWizardData(data)
    setAppData({ [LOCAL_STORAGE_WIZARD_DATA]: data })
  }
  const resetWizardData = () => {
    setWizardData(WIZARD_DATA_INITIAL_STATE)
    setAppData({
      [LOCAL_STORAGE_WIZARD_DATA]: WIZARD_DATA_INITIAL_STATE
    })
  }
  const WIZARD_SAVED_DATA_INITIAL_STATE: IWizardSavedData = {
    in_process_profile_information: {
      hasData: false,
      data: null
    },
    in_process_profile_documents: {
      hasData: false,
      data: null
    },
    in_process_contract: {
      hasData: false,
      data: null
    }
  }
  const [wizardSavedData, setWizardSavedData] = useState(WIZARD_SAVED_DATA_INITIAL_STATE)
  const updateWizardSavedData = (data: any) => {
    const NEW_DATA = {
      ...wizardSavedData,
      ...data
    }
    setWizardSavedData(NEW_DATA)
    setAppData({
      [LOCAL_STORAGE_WIZARD_SAVED_DATA]: NEW_DATA
    })
  }
  const resetWizardSavedData = () => {
    setWizardSavedData(WIZARD_SAVED_DATA_INITIAL_STATE)
    setAppData({
      [LOCAL_STORAGE_WIZARD_SAVED_DATA]: WIZARD_SAVED_DATA_INITIAL_STATE
    })
  }
  const wizardPutStageOnPlate = (move: string, data?: any) => {
    const CURRENT_INDEX = WIZARD_STAGES_SEQUENCE.findIndex((stage: any) => stage === wizardData.stageOnPlate.name)

    const INITIAL_INDEX = 0
    let newIndex = CURRENT_INDEX

    if (move === 'next') {
      newIndex = CURRENT_INDEX > -1 ? newIndex + 1 : INITIAL_INDEX
    }

    if (move === 'previous') {
      newIndex = CURRENT_INDEX > 0 ? newIndex - 1 : INITIAL_INDEX
    }

    updateWizardData({
      ...wizardData,
      stageOnPlate: {
        id: newIndex,
        name: WIZARD_STAGES_SEQUENCE[newIndex]
      },
      ...data
    })
  }

  const COMPANY = {
    company,
    companyReset,
    companyStages,
    companyUpdate,
    isFirstLoadEntity,
    registerStep,
    registration,
    resetRegistration,
    resetSelectedCompany,
    resetWizardData,
    resetWizardSavedData,
    saveRegistration,
    selectedCompany,
    updateCompanyStages,
    updateSelectedCompany,
    updateWizardData,
    updateWizardSavedData,
    wizardData,
    wizardPutStageOnPlate,
    wizardSavedData,
    currentCompanyReference,
    updateCurrentCompanyReference
  }

  // SIDE PANEL ········································································································
  const SIDE_PANEL_INITIAL_VALUE = undefined
  const [sidePanelContent, setSidePanelContent] = useState<ISidePanelContent | undefined>(SIDE_PANEL_INITIAL_VALUE)
  const createSidePanel = (children: React.ReactChild, data: any) => setSidePanelContent({ children, data })
  const removeSidePanel = () => setSidePanelContent(SIDE_PANEL_INITIAL_VALUE)
  const SIDE_PANEL = { createSidePanel, sidePanelContent, removeSidePanel }

  // NOTIFICATION CENTER ·······························································································
  const [hideNotificationCenterCompany, setHideNotificationCenterCompany] = useState<boolean>(true)
  const changeHideNotificationCenterCompany = (value: boolean) => setHideNotificationCenterCompany(value)
  const NOTIFICATION_CENTER = { hideNotificationCenterCompany, changeHideNotificationCenterCompany }

  const getNotificationsDataFromStateOrLocalStorage = (): string[] => {
    const NOTIFICATIONS_DATA_IN_LOCALSTORAGE = LOCAL_STORAGE_APP_STATE[LOCAL_STORAGE_NOTIFICATIONS] || []
    return NOTIFICATIONS_DATA_IN_LOCALSTORAGE
  }
  const [notifications, setNotifications] = useState(getNotificationsDataFromStateOrLocalStorage())
  const [showNotificationsAfterTour, setShowNotificationsAfterTour] = useState<boolean>(false)
  const updateNotifications = (notificationId: string): void => {
    const NEW_NOTIFICATION_DATA = [...notifications, notificationId]
    setNotifications(NEW_NOTIFICATION_DATA)
    setAppData({ [LOCAL_STORAGE_NOTIFICATIONS]: NEW_NOTIFICATION_DATA })
  }
  const updateShowNotificationsAfterTour = (value: boolean) => setShowNotificationsAfterTour(value)
  const NOTIFICATIONS = {
    notifications,
    updateNotifications,
    showNotificationsAfterTour,
    updateShowNotificationsAfterTour
  }

  // PAYMENT LINKS FILTERS BY COMPANY ··················································································
  const [paymentLinksFiltersByCompany, setPaymentLinksFiltersByCompany] = useState<IPaymentLinksFiltersByCompany>({})
  const changePaymentLinksFiltersByCompany = (value: IPaymentLinksFiltersByCompany) =>
    setPaymentLinksFiltersByCompany({ ...value })
  const PAYMENT_LINKS_FILTERS_BY_COMPANY = { paymentLinksFiltersByCompany, changePaymentLinksFiltersByCompany }

  // USER ..............................................................................................................
  const [user, setUser] = useState(getUserFromStateOrLocalStorage(LOCAL_STORAGE_APP_STATE, LOCAL_STORAGE_USER))
  const userReset = () => {
    setUser(USER_INITIAL_STATE)
    setAppData({ [LOCAL_STORAGE_USER]: USER_INITIAL_STATE })
  }
  const userUpdate = (values: any) => {
    const NEW_USER_DATA = { ...user, ...values }
    setUser(NEW_USER_DATA)
    setAppData({ [LOCAL_STORAGE_USER]: NEW_USER_DATA })
  }
  const USER = { userReset, userUpdate, user }

  return (
    <AppContext.Provider
      value={{
        ...API,
        ...APP_DATA_CONTEXT,
        ...COMPANY,
        ...DASHBOARD_ACCEPTANCE_SETTINGS_BY_COMPANY,
        ...DASHBOARD_RISK_SETTINGS_BY_COMPANY,
        ...DASHBOARD_SALES_SETTINGS_BY_COMPANY,
        ...ESIGN,
        ...FLAGS,
        ...FOOTER,
        ...NOTIFICATION_CENTER,
        ...NOTIFICATIONS,
        ...PAYMENT_LINKS_FILTERS_BY_COMPANY,
        ...PLAYGROUND,
        ...SIDE_PANEL,
        ...USER
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export { AppContext, AppContextProvider }
